import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner, Toast, useStateContext } from '@veda/dashboard-ui';
import { MdCampaign } from 'react-icons/md';
import { usePppApi } from '../../ppp/services/pppApi';

const STATO_CFG = {
  pubblicato: { cls: 'bg-green-100 text-green-700' },
  scaduto:    { cls: 'bg-red-100 text-red-700' },
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('it-IT');
}

function scadenzaGiorni(termineProposte) {
  if (!termineProposte) return null;
  const giorni = Math.round((new Date(termineProposte) - new Date()) / 86400000);
  if (giorni < 0)  return { label: `Scaduto da ${Math.abs(giorni)} gg`, cls: 'text-red-600' };
  if (giorni <= 7) return { label: `Scade tra ${giorni} gg`, cls: 'text-red-600' };
  if (giorni <= 15)return { label: `Scade tra ${giorni} gg`, cls: 'text-amber-600' };
  return               { label: `${giorni} gg alla scadenza`, cls: 'text-blue-600' };
}

export default function OperatoreAvvisiPage() {
  const api = usePppApi();
  const navigate = useNavigate();
  const { _locate, addToast } = useStateContext();

  const [avvisi, setAvvisi]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [interesseIds, setInteresseIds] = useState({});
  const [soloInteresse, setSoloInteresse] = useState(false);

  const carica = useCallback(() => {
    setLoading(true);
    api.listAvvisiOperatore({
      onSuccess: (data) => {
        const list = Array.isArray(data) ? data : [];
        setAvvisi(list);
        const ids = {};
        list.forEach((a) => { if (a.interesseRegistrato) ids[a.id] = true; });
        setInteresseIds(ids);
        setLoading(false);
      },
      onError: () => setLoading(false),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const registraInteresse = (avvisoId, e) => {
    e.stopPropagation();
    api.registraInteresseAvviso(avvisoId, {
      onSuccess: () => {
        addToast(<Toast duration={4} content={_locate('ppp_avviso_operatore_interesse_ok') ?? 'Interesse registrato'} />);
        setInteresseIds((prev) => ({ ...prev, [avvisoId]: true }));
      },
    });
  };

  if (loading) return <div className="flex items-center justify-center min-h-64"><Spinner /></div>;

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300">
            <MdCampaign size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
              {_locate('ppp_avviso_titolo_lista') ?? 'Avvisi pubblici PPP'}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {_locate('ppp_avviso_operatore_lista_hint') ?? 'Avvisi pubblicati dalla Pubblica Amministrazione (art. 193 D.lgs 36/2023)'}
            </p>
          </div>
        </div>
        <button
          onClick={() => setSoloInteresse((v) => !v)}
          className={`text-xs px-3 py-1.5 rounded-full font-medium border transition-colors shrink-0 ${
            soloInteresse
              ? 'bg-green-600 text-white border-green-600'
              : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-green-500 hover:text-green-600'
          }`}
        >
          {soloInteresse ? '✓ Solo con interesse' : 'Solo con interesse'}
        </button>
      </div>

      {(() => {
        const lista = soloInteresse ? avvisi.filter((a) => interesseIds[a.id]) : avvisi;
        if (lista.length === 0) return (
          <div className="text-center py-16 text-gray-400">
            <MdCampaign size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">
              {soloInteresse
                ? 'Nessun avviso con interesse registrato.'
                : (_locate('ppp_avviso_operatori_nessuno') ?? 'Nessun avviso pubblicato al momento.')}
            </p>
          </div>
        );
        return (
        <div className="grid grid-cols-1 gap-4">
          {lista.map((avviso) => {
            const scadenza = scadenzaGiorni(avviso.terminePresentazioneProposte);
            const statoCfg = STATO_CFG[avviso.stato] ?? { cls: 'bg-gray-100 text-gray-600' };
            const giaInteresse = interesseIds[avviso.id];

            return (
              <div
                key={avviso.id}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate(`/operatore/avvisi/${avviso.id}`)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${statoCfg.cls}`}>
                        {_locate(`ppp_avviso_stato_${avviso.stato}`) ?? avviso.stato}
                      </span>
                      {avviso.versione > 1 && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                          v{avviso.versione}
                        </span>
                      )}
                      {avviso.sopraSogliaEuropea && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                          Sopra soglia UE
                        </span>
                      )}
                    </div>
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1 truncate">
                      {avviso.oggetto}
                    </h2>
                    {avviso.enteConcedente && (
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5">{avviso.enteConcedente}</p>
                    )}
                    {avviso.procedimentoTitolo && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{avviso.procedimentoTitolo}</p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-gray-400 flex-wrap">
                      <span>Pubblicato: {formatDate(avviso.dataPubblicazione)}</span>
                      <span>Termine proposte: {formatDate(avviso.terminePresentazioneProposte)}</span>
                      {scadenza && (
                        <span className={`font-medium ${scadenza.cls}`}>{scadenza.label}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => registraInteresse(avviso.id, e)}
                      disabled={giaInteresse}
                      className={`text-xs px-3 py-1.5 rounded font-medium transition-colors ${
                        giaInteresse
                          ? 'bg-green-100 text-green-700 cursor-default'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {giaInteresse
                        ? (_locate('ppp_avviso_operatore_gia_registrato') ?? 'Interesse registrato')
                        : (_locate('ppp_avviso_operatore_interesse') ?? 'Registra interesse')}
                    </button>
                    <button
                      onClick={() => navigate(`/operatore/avvisi/${avviso.id}`)}
                      className="text-xs px-3 py-1.5 rounded font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                      Dettaglio →
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        );
      })()}
    </div>
  );
}
